import type { Animation } from "../../engine/animation/AnimationQueue";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";

// Phase timing (ms)
const LAUNCH_MS = 280;   // hook flies out
const IMPACT_MS = 90;    // brief pause at target, scale bump
const YANK_MS   = 380;   // unit dragged toward (and behind) caster
const FADE_MS   = 140;   // chain fades

const IMPACT_START = LAUNCH_MS;
const YANK_START   = IMPACT_START + IMPACT_MS;
const FADE_START   = YANK_START + YANK_MS;
const TOTAL        = FADE_START + FADE_MS;

const LINK_W = 8;
const LINK_GAP = 3;

function easeOutQuad(t: number): number { return 1 - (1 - t) * (1 - t); }
function easeInQuad(t: number):  number { return t * t; }

export class HookVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  private snapFired = false;
  onEnd?: () => void;
  /** Fires when the yank is done — CombatAnimator should snap visual pos and unfreeze. */
  onYankComplete?: () => void;

  constructor(
    private casterPos: Point,
    private originPos: Point,   // frozen original position of the target unit
    private pullDestPos: Point, // where the unit ends up after pull
    private onImpact: () => void,
    private targetVisual: UnitVisual,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);

    // Impact: scale bump + fire damage popup
    if (!this.impactFired && this.elapsed >= IMPACT_START) {
      this.impactFired = true;
      this.onImpact();
      this.targetVisual.scale = 1.2;
    }

    // Scale settles back to 1 during impact pause
    if (this.elapsed >= IMPACT_START && this.elapsed < YANK_START) {
      const t = (this.elapsed - IMPACT_START) / IMPACT_MS;
      this.targetVisual.scale = 1.2 - t * 0.2;
    }

    // Yank: directly drive the unit's visual position
    if (this.elapsed >= YANK_START && this.elapsed < FADE_START) {
      const t = easeInQuad((this.elapsed - YANK_START) / YANK_MS);
      this.targetVisual.x = this.originPos.x + (this.pullDestPos.x - this.originPos.x) * t;
      this.targetVisual.y = this.originPos.y + (this.pullDestPos.y - this.originPos.y) * t;
    }

    // Snap to final position and hand control back to CombatAnimator
    if (!this.snapFired && this.elapsed >= FADE_START) {
      this.snapFired = true;
      this.targetVisual.scale = 1;
      this.onYankComplete?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed;

    // Current visual position of the hook head
    let headX: number;
    let headY: number;
    let alpha = 1;

    if (t < IMPACT_START) {
      const progress = easeOutQuad(t / LAUNCH_MS);
      headX = this.casterPos.x + (this.originPos.x - this.casterPos.x) * progress;
      headY = this.casterPos.y + (this.originPos.y - this.casterPos.y) * progress;
    } else if (t < YANK_START) {
      headX = this.targetVisual.x;
      headY = this.targetVisual.y;
    } else if (t < FADE_START) {
      headX = this.targetVisual.x;
      headY = this.targetVisual.y;
    } else {
      const ft = (t - FADE_START) / FADE_MS;
      alpha = 1 - ft;
      headX = this.casterPos.x;
      headY = this.casterPos.y;
    }

    const dx = headX - this.casterPos.x;
    const dy = headY - this.casterPos.y;
    const len = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx);

    ctx.save();
    ctx.globalAlpha = alpha;

    // Chain links
    if (len > 2) {
      ctx.save();
      ctx.translate(this.casterPos.x, this.casterPos.y);
      ctx.rotate(angle);

      for (let x = 0; x < len; x += LINK_W + LINK_GAP) {
        const w = Math.min(LINK_W, len - x);
        if (w <= 0) break;
        // Outer dark gold
        ctx.fillStyle = "#8a6210";
        ctx.shadowColor = "#ffc040";
        ctx.shadowBlur = 8;
        ctx.fillRect(x, -3.5, w, 7);
        // Inner bright highlight
        ctx.fillStyle = "#ffe060";
        ctx.shadowBlur = 0;
        ctx.fillRect(x + 1, -1.5, Math.max(0, w - 2), 3);
      }
      ctx.restore();
    }

    // Hook head — diamond pointing in travel direction
    if (t < FADE_START) {
      ctx.save();
      ctx.translate(headX, headY);
      // During yank the hook is pulling backward, so flip 180°
      ctx.rotate(t >= YANK_START ? angle + Math.PI : angle);
      ctx.shadowColor = "#ffd060";
      ctx.shadowBlur = 18;

      // Outer body
      ctx.fillStyle = "#b09030";
      ctx.beginPath();
      ctx.moveTo(13, 0);
      ctx.lineTo(2, -6);
      ctx.lineTo(-3, 0);
      ctx.lineTo(2, 6);
      ctx.closePath();
      ctx.fill();

      // Bright core
      ctx.fillStyle = "#ffe880";
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(11, 0);
      ctx.lineTo(3, -3);
      ctx.lineTo(0, 0);
      ctx.lineTo(3, 3);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
